import { useMemo, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BREEDS } from "@/lib/horseOptions";

export const COLOURS = ["Bay", "Dark Bay", "Blood Bay", "Brown", "Black", "Sorrel", "Chestnut", "Liver Chestnut", "Red Dun", "Dun", "Grullo",
  "Buckskin", "Palomino", "Grey", "Flea-Bitten Grey", "Dapple Grey", "Roan", "Blue Roan", "Red Roan", "Bay Roan", "Strawberry Roan",
  "Cremello", "Perlino", "Smoky Black", "Paint", "Tobiano", "Overo", "Appaloosa", "Leopard", "Blanket", "White"];
const norm = (s) => String(s || "").toLowerCase().replace(/gray/g, "grey").replace(/colou?r/g, "").replace(/[^a-z]/g, "");
export const canonColour = (v) => COLOURS.find((c) => norm(c) === norm(v));

// Same breed list as the Horse Application ("Other" becomes type-your-own).
export const BREED_LABELS = BREEDS.filter(([k]) => k !== "other").map(([, l]) => l);
const bnorm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Searchable dropdown with an "Other (type your own)" option; unknown saved values show as Other.
function SearchSelect({ id, label, options, canon, match, value, onChange, disabled, placeholder, search, prefix, otherHint, required, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const c = canon(value);
  const [other, setOther] = useState(!!value && !c);
  const list = useMemo(() => options.filter((o) => !q.trim() || match(o, q)), [q, options, match]);
  const pick = (o) => { setOpen(false); setQ(""); if (o === "__other") { setOther(true); onChange(c ? "" : value || ""); } else { setOther(false); onChange(o); } };
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">{label}{required && <span className="text-red-600"> *</span>}</label>
      {disabled ? <Input id={id} data-testid={id} value={value || ""} readOnly /> : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" id={id} data-testid={id} aria-invalid={!!error} className={`w-full h-10 px-3 rounded-md border ${error ? "border-red-400" : "border-stone-200"} bg-white text-left text-sm flex items-center justify-between`}>
              <span className={c || other ? "text-stone-900" : "text-stone-400"}>{other ? "Other" : c || placeholder}</span><ChevronDown className="h-4 w-4 text-stone-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
            <Input data-testid={`${prefix}-search`} autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={search} className="h-9 mb-2" />
            <ul role="listbox" className="max-h-60 overflow-y-auto">
              {list.map((o) => (
                <li key={o}><button type="button" role="option" aria-selected={c === o} data-testid={`${prefix}-option-${norm(o) || bnorm(o)}`} onClick={() => pick(o)}
                  className="w-full text-left px-2 py-2 rounded text-sm hover:bg-amber-50 flex items-center gap-2">{c === o && <Check className="h-4 w-4 text-amber-800" />}{o}</button></li>
              ))}
              <li><button type="button" data-testid={`${prefix}-option-other`} onClick={() => pick("__other")} className="w-full text-left px-2 py-2 rounded text-sm hover:bg-amber-50 italic">Other (type your own)</button></li>
            </ul>
          </PopoverContent>
        </Popover>
      )}
      {other && !disabled && (
        <Input data-testid={`${id}_other`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={otherHint} className="mt-2 border-stone-200" />
      )}
      {error && <p data-testid={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

const colourMatch = (c, q) => norm(c).includes(norm(q));
export function ColourField(props) {
  return <SearchSelect id="field-horse-horse_color" label="Colour" options={COLOURS} canon={canonColour} match={colourMatch} placeholder="Select colour"
    search="Search colour (e.g. gray, bay)" prefix="colour" otherHint="Describe the colour" {...props} />;
}

const canonBreed = (v) => BREED_LABELS.find((b) => bnorm(b) === bnorm(v));
const breedMatch = (b, q) => bnorm(b).includes(bnorm(q));
export function BreedField(props) {
  return <SearchSelect id="field-horse-breed" label="Breed" options={BREED_LABELS} canon={canonBreed} match={breedMatch} placeholder="Select breed"
    search="Search breed (e.g. quarter, paint)" prefix="breed" otherHint="Type the breed" {...props} />;
}
